import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Anchor navigation component providing smooth scrolling between page sections
 * Uses scroll position tracking for reliable section detection
 *
 * @component
 */
const AnchorNavigationArrows = () => {
  const [currentSection, setCurrentSection] = useState(0);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const [sections, setSections] = useState([]);
  const location = useLocation();

  // Check if we're on the homepage
  const isHomepage = location.pathname === "/" || location.pathname === process.env.PUBLIC_URL + "/";

  useEffect(() => {
    // Define sections to navigate between
    const sectionSelectors = [
      "#hero-section",
      "#products-section",
      "#content-section",
      "#testimonials-section",
      "#footer-section",
    ];

    // Keep track of observer for cleanup
    let observer;

    // Delay to allow conditional sections to render
    const initTimeout = setTimeout(() => {
      // Find existing sections on the page
      const foundSections = sectionSelectors.map((selector) => document.querySelector(selector)).filter(Boolean);

      setSections(foundSections);

      if (foundSections.length === 0) {
        return;
      }

      // Track visibility ratio of each section
      const visibilityMap = new Map();
      foundSections.forEach((_, index) => visibilityMap.set(index, 0));

      // Use IntersectionObserver to track which section is most visible
      const observerOptions = {
        root: null,
        rootMargin: "0px",
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0], // Fine-grained tracking
      };

      const updateCurrentSection = () => {
        // Find section with highest visibility ratio
        let maxRatio = -1;
        let mostVisibleSection = 0;

        visibilityMap.forEach((ratio, index) => {
          if (ratio > maxRatio) {
            maxRatio = ratio;
            mostVisibleSection = index;
          }
        });

        // Use functional setState to avoid stale closure
        setCurrentSection(() => mostVisibleSection);

        // Check if footer (last section) is significantly visible
        const footerIndex = foundSections.length - 1;
        const footerRatio = visibilityMap.get(footerIndex) || 0;
        const atBottom = footerRatio >= 0.5;

        // Use functional setState to avoid stale closure
        setIsAtBottom(() => atBottom);
      };

      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const sectionIndex = foundSections.findIndex((section) => section === entry.target);

          if (sectionIndex !== -1) {
            visibilityMap.set(sectionIndex, entry.intersectionRatio);
          }
        });

        updateCurrentSection();
      }, observerOptions);

      // Observe all sections
      foundSections.forEach((section) => observer.observe(section));

      // Initial update
      updateCurrentSection();
    }, 200); // 200ms delay to allow sections to render

    return () => {
      clearTimeout(initTimeout);
      if (observer) {
        observer.disconnect();
      }
    };
  }, [location]);

  const scrollToSection = () => {
    if (sections.length === 0) {
      return;
    }

    if (isAtBottom) {
      // If at bottom, scroll back to top
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else {
      // Scroll to next section
      const targetIndex = currentSection + 1;

      if (targetIndex < sections.length) {
        const targetSection = sections[targetIndex];
        targetSection.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      } else {
        // If at last section, scroll to bottom
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: "smooth",
        });
      }
    }
  };

  // Don't render if no sections found
  if (sections.length <= 1) return null;

  return (
    <div className={`anchor-navigation-arrows ${isHomepage ? "homepage" : ""}`}>
      {/* Single navigation arrow */}
      <button
        className="nav-arrow"
        onClick={scrollToSection}
        aria-label={isAtBottom ? "Scroll to top" : "Scroll to next section"}
      >
        {isAtBottom ? (
          <svg width="29" height="29" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 19V5M5 12L12 5L19 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          <svg width="29" height="29" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 5V19M5 12L12 19L19 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>
    </div>
  );
};

export default AnchorNavigationArrows;
